import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { AssetRemoveDocument } from 'src/graphql/actions/asset/assetRemove.mutation';
import { Button } from '@/components/ui/button';
import { ClientError, getApiError } from 'src/utils/errors';

import { useAssetsContext } from '../model';
import { refetchAssets } from '../api';

const RemoveAssetDialog = () => {
  const {
    ui: {
      removeDialog: { isOpen, assetId },
    },
    closeRemoveDialog,
  } = useAssetsContext();
  const [removeAsset, { loading }] = useMutation(AssetRemoveDocument, {
    onCompleted: refetchAssets,
  });

  const onDestroyAsset = async () => {
    if (!assetId) return;

    try {
      const { data } = await removeAsset({
        variables: {
          assetId,
        },
      });

      if (data?.assetRemove?.success) {
        toast.success('Asset removed', {
          description: 'The asset is no longer included in your escrow material.',
        });
        closeRemoveDialog();
      } else {
        throw new ClientError(data?.assetRemove?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      closeRemoveDialog();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to remove this asset?</AlertDialogTitle>
          <AlertDialogDescription>
            The asset and its entire version history will be deleted from our vaults.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={onDestroyAsset} isLoading={loading}>
            Remove
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RemoveAssetDialog;
